import { MenuItem } from '@mui/material';
import { useField, Field } from 'formik';
import { FC } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { hexColorValidationSchema, useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, Button as TButton } from '../../types/form-fields';
import { ColorPickerField, FormRow, InputField as FormikInputField } from '../fields';
import { FormBlock } from './common';

export const isButtonField = (field: FormField): field is TButton => {
  return field.type === FieldType.Button;
};

export const defaultButtonFieldProps: TButton = {
  type: FieldType.Button,
  text: 'Button text',
  bgColor: '#3899ec',
  textColor: '#fff',
  width: 'full',
  size: 'large',
};

type ButtonFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

const textValueSchema = Yup.string().optional().max(255, 'Should be < 255 characters');

const ButtonFieldSettings: FC<ButtonFieldSettingsProps> = ({ name, remove }) => {
  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  const { t } = useTranslation();

  return (
    <ButtonFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.text`}
          component={FormikInputField}
          label={t('pages.campaign.checkboxField.text')}
          validate={(value: string) => validate(value, textValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.size`}
          component={FormikInputField}
          label={t('pages.campaign.checkboxField.size')}
          select
          defaultValue="400"
        >
          {['small', 'medium', 'large'].map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </Field>
      </FormRow>

      <FormRow>
        <Field name={`${name}.width`} component={FormikInputField} label={t('pages.campaign.checkboxField.width')} select defaultValue="full">
          {['full', 'auto'].map((width) => (
            <MenuItem key={width} value={width}>
              {width}
            </MenuItem>
          ))}
        </Field>
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.textColor`}
          component={ColorPickerField}
          label={t('pages.campaign.checkboxField.textColor')}
          validate={(value: string) => validate(value, hexColorValidationSchema)}
        />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.bgColor`}
          component={ColorPickerField}
          label={t('pages.campaign.rouletteSettings.texts.backgroundColor')}
          validate={(value: string) => validate(value, hexColorValidationSchema)}
        />
      </FormRow>
    </ButtonFieldSettingsWrapper>
  );
};

type ButtonFieldProps = {
  name: string;
  remove?: () => void;
};

export const ButtonField: FC<ButtonFieldProps> = ({ name, remove }) => {
  const [field] = useField<TButton>(name);
  const dispatch = useAppDispatch();

  const {
    value: { text, textColor, bgColor, size, width },
  } = field;

  const handleClick = () => {
    dispatch(
      openSidebar({
        content: <ButtonFieldSettings name={name} remove={remove} />,
      })
    );
  };

  return (
    <FormBlock onClick={handleClick}>
      <Cover />

      <FormButton size={size} textColor={textColor} bgColor={bgColor} width={width}>
        {text}
      </FormButton>
    </FormBlock>
  );
};

const ButtonFieldSettingsWrapper = styled.div``;

const Cover = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0 0 0 0;
  cursor: pointer;
`;

const FormButton = styled.button<{
  textColor: string;
  bgColor: string;
  size: 'small' | 'medium' | 'large';
  width: 'auto' | 'full';
}>`
  border: none;
  margin: 0;
  padding: 0;
  width: ${(p) => (p.width === 'auto' ? 'auto' : '100%')};
  overflow: visible;
  background-color: ${(p) => p.bgColor};
  color: ${(p) => p.textColor};
  font: inherit;
  line-height: normal;
  appearance: none;
  outline: none;
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  align-self: ${(p) => (p.width === 'auto' ? 'center' : 'stretch')};
  text-align: center;
  cursor: pointer;
  transition: background-color 0.15s linear;

  :hover {
    filter: brightness(95%);
  }

  :active {
    filter: brightness(90%);
  }

  ${(p) => {
    switch (p.size) {
      case 'small':
        return css`
          font-size: 13px;
          padding: 4px 10px;
        `;
      case 'medium':
        return css`
          font-size: 14px;
          padding: 6px 16px;
        `;
      case 'large':
        return css`
          font-size: 15px;
          padding: 8px 22px;
        `;
    }
  }};
`;
